/**
 * useCanvasCallbacks Hook
 * Consolidates duplicate callback creation logic from PlayCanvas, ConceptCanvas, and FormationCanvas
 *
 * Provides standard implementations for all BaseCanvasCallbacks operations:
 * - Drawing operations (add, update, delete, change)
 * - Player operations (add, move, delete, fill, label)
 * - Linking operations (link/unlink drawing to player)
 * - Pre-snap motion operations
 */

import { useMemo } from 'react'
import type { BaseCanvasCallbacks, Player, PreSnapMovement } from '../BaseCanvas.types'
import type { Drawing } from '@/types'

interface UseCanvasCallbacksOptions {
	/** Setter for drawings - uses functional updates internally */
	setDrawings: (drawings: Drawing[] | ((prev: Drawing[]) => Drawing[])) => void
	/** Setter for players - uses functional updates internally */
	setPlayers: (players: Player[] | ((prev: Player[]) => Player[])) => void

	/**
	 * Optional custom handler for player movement
	 * If provided, overrides default setPlayers behavior
	 * Useful for PlayContext dispatch integration
	 */
	onPlayerMoveOverride?: (id: string, x: number, y: number) => void

	/**
	 * If true, drawing operations become no-ops
	 * Useful for FormationCanvas which only handles players
	 */
	disableDrawings?: boolean
}

/**
 * Creates standardized BaseCanvasCallbacks object with functional state updates
 * to prevent stale closures. Eliminates ~180 lines of duplicated code across
 * 3 canvas components (PlayCanvas, ConceptCanvas, FormationCanvas).
 *
 * All callbacks use functional updates (prev => ...) to ensure they always
 * work with the latest state, preventing React stale closure bugs.
 *
 * @param options - Configuration options
 * @param options.setDrawings - Setter for drawings array (supports functional updates)
 * @param options.setPlayers - Setter for players array (supports functional updates)
 * @param options.onPlayerMoveOverride - Optional custom player move handler (for PlayContext dispatch)
 * @param options.disableDrawings - If true, drawing operations become no-ops (for FormationCanvas)
 * @returns Memoized BaseCanvasCallbacks object with stable callbacks
 *
 * @example
 * ```tsx
 * const callbacks = useCanvasCallbacks({
 *   setDrawings,
 *   setPlayers,
 *   onPlayerMoveOverride: (id, x, y) => {
 *     dispatch({ type: 'UPDATE_PLAYER', id, updates: { x, y } })
 *   }
 * })
 * ```
 */
export function useCanvasCallbacks({
									   setDrawings,
									   setPlayers,
									   onPlayerMoveOverride,
									   disableDrawings = false,
								   }: UseCanvasCallbacksOptions): BaseCanvasCallbacks {
	return useMemo<BaseCanvasCallbacks>(() => {
		// No-op function for disabled operations
		const noop = () => {
		}

		return {
			// Drawing operations - use functional updates to avoid stale closures
			onDrawingsChange: disableDrawings ? noop : (newDrawings: Drawing[]) => {
				setDrawings(newDrawings)
			},
			onDrawingAdd: disableDrawings ? noop : (drawing: Drawing) => {
				setDrawings(prev => [...prev, drawing])
			},
			onDrawingUpdate: disableDrawings ? noop : (id: string, updates: Partial<Drawing>) => {
				setDrawings(prev =>
					prev.map((d) => (d.id === id ? { ...d, ...updates } : d)),
				)
			},
			onDrawingDelete: disableDrawings ? noop : (id: string) => {
				setDrawings(prev => prev.filter((d) => d.id !== id))
			},

			// Player operations - use functional updates
			onPlayersChange: (newPlayers: Player[]) => {
				setPlayers(newPlayers)
			},
			onPlayerAdd: async (player: Player): Promise<string> => {
				setPlayers(prev => [...prev, player])
				return player.id
			},
			onPlayerMove: (id: string, x: number, y: number) => {
				if (onPlayerMoveOverride) {
					onPlayerMoveOverride(id, x, y)
				} else {
					setPlayers(prev =>
						prev.map((p) => (p.id === id ? { ...p, x, y } : p)),
					)
				}
			},
			onPlayerDelete: (id: string) => {
				setPlayers(prev => prev.filter((p) => p.id !== id))
			},
			onPlayerFill: (id: string, color: string) => {
				setPlayers(prev =>
					prev.map((p) => (p.id === id ? { ...p, color } : p)),
				)
			},
			onPlayerLabelChange: (id: string, label: string) => {
				setPlayers(prev =>
					prev.map((p) => (p.id === id ? { ...p, label } : p)),
				)
			},

			// Linking operations (handled by BaseCanvas through onDrawingsChange)
			onLinkDrawingToPlayer: disableDrawings ? noop : () => {
			},
			onUnlinkDrawingFromPlayer: disableDrawings ? noop : () => {
			},

			// Pre-snap operations - use functional updates
			onPreSnapUpdate: disableDrawings ? noop : (drawingId: string, preSnap: PreSnapMovement | null) => {
				setDrawings(prev =>
					prev.map((d) =>
						d.id === drawingId
							? { ...d, preSnapMotion: preSnap || undefined }
							: d,
					),
				)
			},
		}
	}, [setDrawings, setPlayers, onPlayerMoveOverride, disableDrawings])
}
